import { useReactFlow } from '@xyflow/react';
import { formatDistanceToNow } from 'date-fns';
import { Users, MessageSquare, Clipboard, CheckSquare, Pin, PinOff, ChevronRight, ChevronLeft } from 'lucide-react';
import React, { useRef, KeyboardEvent, useState, useEffect } from 'react';

import styles from '@/components/nodes/presence/SidePanel/SidePanel.module.css';
import { nodeColor } from '@/constants/nodeTypes';
import { useUser } from '@/context/UserContext';
import { useSidePanelState } from '@/hooks/useSidePanelState';

interface SidebarPresenceProps {
  nodeId?: string;
  nodeData?: any;
}

interface NotificationStatus {
  chat: boolean;
  notes: boolean;
  tasks: boolean;
  lastVisited: Record<string, {
      lastChat: number;
      lastNotes: number;
      lastTasks: number;
    }>
}

export function SidebarPresence({ nodeId, nodeData }: SidebarPresenceProps) {
  const { 
    state: panelState, 
    setActiveTab,
    togglePanel,
    pinNode,
    isPinned,
    setWidth  // Make sure this is exposed by the hook
  } = useSidePanelState();
  
  const { updateNodeData, getNode } = useReactFlow();
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const noteTextareaRef = useRef<HTMLTextAreaElement>(null);
  // Add a ref to track mounted state
  const isMounted = useRef<boolean>(true);
  const user = useUser();
  const [chatInput, setChatInput] = useState('');
  const [notes, setNotes] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [notifications, setNotifications] = useState<NotificationStatus>(() => {
    try {
      const savedData = localStorage.getItem('sidebar-notifications');
      return savedData ? JSON.parse(savedData) : {
        chat: false,
        notes: false,
        tasks: false,
        lastVisited: {}
      };
    } catch (e) {
      return {
        chat: false,
        notes: false,
        tasks: false,
        lastVisited: {}
      };
    }
  });
  
  // Track component mounted state
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // Animation state for button fade effect
  const [buttonFading, setButtonFading] = useState(false);
  
  // Get relevant data from node or use empty defaults
  const activeUsers = nodeData?.activeUsers || {};
  const chatMessages = nodeData?.chatMessages || [];
  const nodeTasks = nodeData?.tasks || [];
  const systemState = nodeData?.systemState || { lastCheck: Date.now(), isHealthy: true };
  const node = getNode(nodeId || '');  // Determine if sidebar should be shown (not for nodepresence or when no node is selected)
  const shouldShowSidebar = true; // Always show the sidebar
  const shouldFadeOut = false; // Always show the toggle button
  const hasValidNode = !!nodeId && node && node.type !== 'nodepresence';

  // Get node-specific color
  const colorValue = node?.type ? 
    nodeColor({
      type: node.type,
      id: nodeId || '',
      position: { x: 0, y: 0 },
      data: {}
    }) : '#64748b';
  
  // Custom gradient style for the header
  const headerGradientStyle = {
background: `linear-gradient(to top,
  ${colorValue}ff 0%,
  ${colorValue}ff 2px,
  ${colorValue}70 3px,
  ${colorValue}30 30%,
  ${colorValue}01 70%,
  transparent 100%)`,
    height: '60px',
    width: '100%',
    position: 'absolute' as const,
    top: '24px', // Position it below the header
    left: 0,
    zIndex: 0,
    pointerEvents: 'none' as const,
  };

  // Update notes from node data when node changes
  useEffect(() => {
    if (nodeData?.notes) {
      setNotes(nodeData.notes);
    } else {
      setNotes('');
    }
  }, [nodeData?.notes, nodeId]);

  // Check for new notifications when node data changes - refactored to avoid dependency issues
  useEffect(() => {
    if (!nodeId || !isMounted.current) return;
    
    // Create a stable reference to the current notifications
    const currentNotifications = { ...notifications };
    const lastVisited = currentNotifications.lastVisited[nodeId] || {
      lastChat: 0,
      lastNotes: 0,
      lastTasks: 0
    };

    // Check for new chat messages
    const chatMessages = nodeData?.chatMessages || [];
    const hasNewChat = chatMessages.some(msg => msg.timestamp > lastVisited.lastChat);
    
    // Check for updated notes
    const notesUpdatedAt = nodeData?.notesUpdatedAt || 0;
    const hasNewNotes = notesUpdatedAt > lastVisited.lastNotes;
    
    // Check for new tasks
    const nodeTasks = nodeData?.tasks || [];
    const hasNewTasks = nodeTasks.some(task => 
      task.createdAt > lastVisited.lastTasks || 
      (task.completedAt && task.completedAt > lastVisited.lastTasks)
    );

    // Only update if there's an actual change
    if (hasNewChat !== currentNotifications.chat || 
        hasNewNotes !== currentNotifications.notes || 
        hasNewTasks !== currentNotifications.tasks) {
      setNotifications(prev => ({
        ...prev,
        chat: hasNewChat,
        notes: hasNewNotes,
        tasks: hasNewTasks
      }));
    }
  }, [nodeId, nodeData]);

  // Update last visited timestamps - completely rewritten to fix the infinite loop issue
  const previousActiveTabRef = useRef<string | null>(null);
  
  useEffect(() => {
    if (!nodeId || !isMounted.current) return;
    
    // Skip the first render or if the tab hasn't actually changed
    if (previousActiveTabRef.current === panelState.activeTab) {
      return;
    }
    
    // Update ref with current tab
    previousActiveTabRef.current = panelState.activeTab;
    
    const now = Date.now();
    
    // Use functional update to ensure we have the latest state
    setNotifications(prev => {
      // Clone the previous state
      const lastVisited = {
        ...(prev.lastVisited[nodeId] || {
          lastChat: 0,
          lastNotes: 0,
          lastTasks: 0
        })
      };
      
      // Update only the timestamp for the active tab
      if (panelState.activeTab === 'chat') {
        lastVisited.lastChat = now;
        return {
          ...prev,
          chat: false,
          lastVisited: {
            ...prev.lastVisited,
            [nodeId]: lastVisited
          }
        };
      } else if (panelState.activeTab === 'notes') {
        lastVisited.lastNotes = now;
        return {
          ...prev,
          notes: false,
          lastVisited: {
            ...prev.lastVisited,
            [nodeId]: lastVisited
          }
        };
      } else if (panelState.activeTab === 'tasks') {
        lastVisited.lastTasks = now;
        return {
          ...prev,
          tasks: false,
          lastVisited: {
            ...prev.lastVisited,
            [nodeId]: lastVisited
          }
        };
      }
      
      return prev; // No changes if not a tracked tab
    });
  }, [panelState.activeTab, nodeId]);

  // Save notifications state to localStorage - now with a throttle to avoid excessive writes
  useEffect(() => {
    const handler = setTimeout(() => {
      if (isMounted.current) {
        localStorage.setItem('sidebar-notifications', JSON.stringify(notifications));
      }
    }, 300); // Throttle saving to localStorage
    
    return () => clearTimeout(handler);
  }, [notifications]);

  // Handle button click with animation
  const handleButtonClick = () => {
    setButtonFading(true);
    
    // Toggle panel after slight delay for animation
    setTimeout(() => {
      togglePanel();
      // Reset animation state after animation completes
      setTimeout(() => setButtonFading(false), 300);
    }, 100);
  };

  // Scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && panelState.activeTab === 'chat') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, panelState.activeTab]);

  // Format timestamp to relative time
  const formatTimestamp = (timestamp: number): string => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  // Chat message submission handler
  const handleSubmitMessage = () => {
    if (chatInput.trim() && nodeData && nodeId) {
      const newMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        content: chatInput.trim(),
        email: user.email || 'anonymous',
        timestamp: Date.now()
      };
      
      // Add message to the node's data
      const updatedMessages = [...(nodeData.chatMessages || []), newMessage];
      updateNodeData(nodeId, { 
        ...nodeData, 
        chatMessages: updatedMessages 
      });
      
      // Clear the input field
      setChatInput('');
    }
  };

  // Handle Enter key in chat input
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitMessage();
    }
  };

  // Save notes to node data
  const handleSaveNotes = () => {
    if (nodeId) {
      updateNodeData(nodeId, { 
        ...nodeData, 
        notes: notes,
        notesUpdatedAt: Date.now()
      });
    }
  };

  // Auto-save notes when they change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (nodeId && nodeData && notes !== nodeData.notes && isMounted.current) {
        handleSaveNotes();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [notes, nodeId, nodeData]);

  // Add a new task
  const handleAddTask = () => {
    if (!newTaskTitle.trim() || !nodeId) return;
    
    const newTask = {
      id: `task-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      title: newTaskTitle,
      description: newTaskDesc,
      checked: false,
      createdAt: Date.now(),
      completedAt: null,
      completedBy: null
    };
    
    const updatedTasks = [...(nodeData?.tasks || []), newTask];
    updateNodeData(nodeId, {
      ...nodeData,
      tasks: updatedTasks
    });
    
    // Clear form
    setNewTaskTitle('');
    setNewTaskDesc('');
  };

  // Toggle task completion status
  const handleToggleTask = (taskId: string) => {
    if (!nodeId) return;
    
    const updatedTasks = (nodeData?.tasks || []).map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          checked: !task.checked,
          completedAt: !task.checked ? Date.now() : null,
          completedBy: !task.checked ? user.email : null
        };
      }
      return task;
    });
    
    updateNodeData(nodeId, {
      ...nodeData,
      tasks: updatedTasks
    });
  };

  // Delete a task
  const handleDeleteTask = (taskId: string) => {
    if (!nodeId) return;
    
    const updatedTasks = (nodeData?.tasks || []).filter(task => task.id !== taskId);
    updateNodeData(nodeId, {
      ...nodeData,
      tasks: updatedTasks
    });
  };

  // Handle pinning the current node
  const handlePinNode = () => {
    pinNode(nodeId || null);
  };

  // Custom animation style
  const sidebarAnimationStyle = {
    transition: 'all 420ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  };
  
  // Set sidebar width on component mount
  useEffect(() => {
    // Set the width to 600px (or your desired width)
    // Only set if it's not already that width to avoid unnecessary re-renders
    if (panelState.width !== 420) {
      setWidth(420);
    }
  }, []);  // Empty dependency array means this runs once on mount

  // Calculate sidebar width based on panel state - with forced minimum width
  const sidebarWidth = panelState.isOpen ? `${Math.max(panelState.width, 420)}px` : '2px';

  // Custom shadow style for the sidebar
  const sidebarShadowStyle = panelState.isOpen ? {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.35), 0 0 3px rgba(0, 0, 0, 0.55)',
  } : {};

  return (
    <div
      className={`${panelState.isOpen ? styles.sidePanelOpen : ''} fixed top-0 left-0 h-full z-[1000] pointer-events-none`}
      style={{
        opacity: shouldFadeOut ? 0 : 1,
        transition: 'opacity 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        pointerEvents: shouldFadeOut ? 'none' : 'auto',
      }}
    >
      <div className="pointer-events-auto">
        {/* Fixed position toggle button that stays on the edge - now with fade animation */}
        <div
          className="absolute top-1/2 -translate-y-1/2 z-[1010] flex items-center justify-center bg-white bg-opacity-80 hover:bg-opacity-100 shadow-md w-6 h-20 cursor-pointer transition-all duration-300 border-t border-b border-r border-gray-200 rounded-r-lg"
          style={{
            right: panelState.isOpen ? 'auto' : 'auto',
            left: panelState.isOpen ? sidebarWidth : '0',
            transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            opacity: shouldFadeOut || buttonFading ? 0.2 : 1,
            pointerEvents: shouldFadeOut ? 'none' : 'auto',
          }}
          onClick={shouldFadeOut ? undefined : handleButtonClick}
        >
          {panelState.isOpen ?
            <ChevronLeft size={16} className={`transition-opacity duration-200 ${buttonFading ? 'opacity-0' : 'opacity-100'}`} /> :
            <ChevronRight size={16} className={`transition-opacity duration-200 ${buttonFading ? 'opacity-0' : 'opacity-100'}`} />
          }

          {/* Notification badges */}
          {!panelState.isOpen && (
            <div className="absolute -right-1 flex flex-col gap-1">
              {notifications.chat && (
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              )}
              {notifications.notes && (
                <div className="w-3 h-3 rounded-full bg-amber-600"></div>
              )}
              {notifications.tasks && (
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              )}
            </div>
          )}
        </div>
        {/* Sidebar panel */}
        <div
          className="absolute top-0 left-0 h-full"
          style={{
            ...sidebarAnimationStyle,
            width: sidebarWidth,
            opacity: panelState.isOpen && shouldShowSidebar ? 1 : 0.6,
            pointerEvents: shouldFadeOut ? 'none' : 'auto',
          }}
        >
          <div
            className={`h-full ${panelState.isOpen && shouldShowSidebar ? 'bg-background rounded-r-lg' : 'bg-transparent'}`}
            style={{
              ...sidebarAnimationStyle,
              ...sidebarShadowStyle
            }}
          >
            {panelState.isOpen && shouldShowSidebar && (
              <div className="flex flex-col h-full">
                {/* Header with title and node label */}
                <div className="p-4 border-b border-gray-200 flex justify-between items-center relative">
                  <div>
                    <p className="glitch-text text-xl" data-text="Node Overview & Status">Node Overview & Status</p>
                    {nodeData?.label && (
                      <p className="text-sm text-black-600 mt-1">{nodeData.label}</p>
                    )}
                    <style jsx={'true'}>{`
                      .glitch-text {
                        position: relative;
                        color: rgba(0, 0, 0, 0.9);
                        font-weight: bold;
                        text-shadow:
                          0.5px 0.5px 0 rgba(255, 0, 0, 0.3),
                          -0.5px -0.5px 0 rgba(0, 255, 0, 0.3),
                          0.5px -0.5px 0 rgba(0, 0, 255, 0.3);
                        animation: glitch 32s infinite;
                      }

                      .glitch-text::before,
                      .glitch-text::after {
                        content: attr(data-text);
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        opacity: 0.8;
                      }

                      .glitch-text::before {
                        left: 0.5px;
                        text-shadow: 0.5px 0 rgba(255, 0, 193, 0.5);
                        clip: rect(0, 900px, 0, 0);
                        animation: glitch-anim 8s infinite linear alternate-reverse;
                      }

                      .glitch-text::after {
                        left: -0.5px;
                        text-shadow: -0.5px 0 rgba(0, 255, 249, 0.5);
                        animation: glitch-anim2 12s infinite linear alternate-reverse;
                      }

                      @keyframes glitch {
                        0%, 100% { text-shadow: 0.5px 0.5px 0 rgba(255,0,0,0.3), -0.5px -0.5px 0 rgba(0,255,0,0.3), 0.5px -0.5px 0 rgba(0,0,255,0.3); }
                        25% { text-shadow: -0.5px 0.5px 0 rgba(255,0,0,0.3), 0.5px -0.5px 0 rgba(0,255,0,0.3), -0.5px 0.5px 0 rgba(0,0,255,0.3); }
                        50% { text-shadow: 0.5px -0.5px 0 rgba(255,0,0,0.3), -0.5px 0.5px 0 rgba(0,255,0,0.3), 0.5px 0.5px 0 rgba(0,0,255,0.3); }
                        75% { text-shadow: -0.5px -0.5px 0 rgba(255,0,0,0.3), 0.5px 0.5px 0 rgba(0,255,0,0.3), -0.5px -0.5px 0 rgba(0,0,255,0.3); }
                      }

                      @keyframes glitch-anim {
                        0% { clip: rect(9px, 9999px, 19px, 0); }
                        20% { clip: rect(21px, 9999px, 6px, 0); }
                        40% { clip: rect(3px, 9999px, 15px, 0); }
                        60% { clip: rect(12px, 9999px, 9px, 0); }
                        80% { clip: rect(18px, 9999px, 14px, 0); }
                        100% { clip: rect(7px, 9999px, 16px, 0); }
                      }

                      @keyframes glitch-anim2 {
                        0% { clip: rect(15px, 9999px, 11px, 0); }
                        20% { clip: rect(8px, 9999px, 19px, 0); }
                        40% { clip: rect(14px, 9999px, 5px, 0); }
                        60% { clip: rect(4px, 9999px, 17px, 0); }
                        80% { clip: rect(18px, 9999px, 9px, 0); }
                        100% { clip: rect(12px, 9999px, 15px, 0); }
                      }

                      .glitch-text {
                        filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
                      }
                    `}</style>
                  </div>
                  {nodeId && (
                    <button 
                      onClick={handlePinNode} 
                      className="hover:bg-gray-100 p-1 rounded-md"
                      title={isPinned(nodeId) ? "Unpin node" : "Pin node"}
                    >
                      {isPinned(nodeId) ? (
                        <PinOff size={16} className="text-blue-600" />
                      ) : (
                        <Pin size={16} className="text-gray-500" />
                      )}
                    </button>
                  )}                </div>
                
                {/* Node-specific gradient section - only show when we have a valid node */}
                {hasValidNode && <div style={headerGradientStyle}></div>}
                
                <div className=".sidePanelOpen flex flex-col flex-1 overflow-hidden">
                  {/* Tab Navigation with notification indicators - removed 'Tabs' heading */}
                  <div className="p-2 relative z-10">
                    <ul className="flex w-full min-w-0 flex-col gap-1 mt-1">
                      <li className="relative">
                        <button 
                          className={`flex w-full items-center gap-2 rounded-md p-2 text-left text-sm ${panelState.activeTab === 'users' ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'}`}
                          onClick={() => setActiveTab('users')}
                        >
                          <Users size={16} />
                          <span>Users</span>
                        </button>
                      </li>
                      
                      <li className="relative">
                        <button 
                          className={`flex w-full items-center gap-2 rounded-md p-2 text-left text-sm ${panelState.activeTab === 'chat' ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'}`}
                          onClick={() => setActiveTab('chat')}
                        >
                          <div className="relative">
                            <MessageSquare size={16} />
                            {notifications.chat && (
                              <div className="absolute -right-1 -top-1 w-2 h-2 bg-blue-500 rounded-full"></div>
                            )}
                          </div>
                          <span>Chat</span>
                        </button>
                      </li>
                      
                      <li className="relative">
                        <button 
                          className={`flex w-full items-center gap-2 rounded-md p-2 text-left text-sm ${panelState.activeTab === 'notes' ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'}`}
                          onClick={() => setActiveTab('notes')}
                        >
                          <div className="relative">
                            <Clipboard size={16} />
                            {notifications.notes && (
                              <div className="absolute -right-1 -top-1 w-2 h-2 bg-amber-600 rounded-full"></div>
                            )}
                          </div>
                          <span>Notes</span>
                        </button>
                      </li>
                      
                      <li className="relative">
                        <button 
                          className={`flex w-full items-center gap-2 rounded-md p-2 text-left text-sm ${panelState.activeTab === 'tasks' ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'}`}
                          onClick={() => setActiveTab('tasks')}
                        >
                          <div className="relative">
                            <CheckSquare size={16} />
                            {notifications.tasks && (
                              <div className="absolute -right-1 -top-1 w-2 h-2 bg-purple-500 rounded-full"></div>
                            )}
                          </div>
                          <span>Tasks</span>
                        </button>
                      </li>
                    </ul>
                  </div>
                  
                  {/* System State Indicator */}
                  <div className="px-2">
                    <div className={styles['system-state']}>
                      <div 
                        className={`${styles['system-state-indicator']} ${
                          systemState.isHealthy 
                            ? styles['system-state-healthy'] 
                            : styles['system-state-unhealthy']
                        }`}
                      />
                      <span>
                        {systemState.isHealthy ? 'System healthy' : 'System issues detected'} 
                        {' • '} Last check: {formatTimestamp(systemState.lastCheck)}
                      </span>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="flex-1 overflow-auto p-2">
                    {hasValidNode ? (
                      <>
                        {/* Users Tab */}
                        {panelState.activeTab === 'users' && (
                          <div className={styles['user-list']}>
                            {Object.keys(activeUsers).length === 0 ? (
                              <div className="text-center text-gray-500 py-4">
                                No active users at the moment
                              </div>
                            ) : (
                              Object.entries(activeUsers).map(([userId, user]: [string, any]) => (
                                <div key={userId} className={styles['user-item']}>
                                  <div className={`${styles['user-status']} ${styles[user.status || 'online']}`} />
                                  <div>
                                    <div className="font-medium">{user.email}</div>
                                    <div className="text-sm text-gray-500">
                                      {user.currentNodeId 
                                        ? <span className="text-blue-600">Viewing node: {user.currentNodeId}</span> 
                                        : 'Not viewing any node'}
                                      {' • '}
                                      Last seen: {user.lastSeen ? formatTimestamp(user.lastSeen) : 'Unknown'}
                                      {user.status && <> • Status: <span className="font-medium">{user.status}</span></>}
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}

                        {/* Chat Tab */}
                        {panelState.activeTab === 'chat' && (
                          <div className={styles['chat-container']}>
                            <div className={styles['messages-container']}>
                              {chatMessages.length === 0 ? (
                                <div className="text-center text-gray-500 py-4">
                                  No messages yet. Start the conversation!
                                </div>
                              ) : (
                                chatMessages.map((message: any) => (
                                  <div 
                                    key={message.id} 
                                    className={`${styles.message} ${
                                      message.email === user.email 
                                        ? styles['message-self'] 
                                        : styles['message-other']
                                    }`}
                                  >
                                    <div className="text-xs font-medium mb-1">
                                      {message.email === user.email ? 'You' : message.email}
                                      {' • '}
                                      {formatTimestamp(message.timestamp)}
                                    </div>
                                    <div>{message.content}</div>
                                  </div>
                                ))
                              )}
                              <div ref={messagesEndRef} />
                            </div>
                            <div className={styles['chat-input-container']}>
                              <textarea
                                ref={chatInputRef}
                                className={styles['chat-input']}
                                placeholder="Type a message..."
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                rows={2}
                              />
                              <div className="text-xs text-gray-500 mt-1">
                                Press Enter to send, Shift+Enter for new line
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Notes Tab */}
                        {panelState.activeTab === 'notes' && (
                          <div className={`${styles['user-notes']} flex flex-col h-full`}>
                            <textarea
                              ref={noteTextareaRef}
                              className="w-full h-full p-2 border border-gray-200 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Add your notes here..."
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                            />
                            <div className="text-xs text-gray-500 mt-1 text-right">
                              Notes auto-save after you stop typing
                            </div>
                          </div>
                        )}

                        {/* Tasks Tab */}
                        {panelState.activeTab === 'tasks' && (
                          <div className={`${styles['user-notes']} flex flex-col h-full gap-4`}>
                            <div className="border border-gray-200 rounded-md p-3">
                              <h3 className="font-medium mb-2">New Task</h3>
                              <div className="flex flex-col gap-2">
                                <input
                                  type="text"
                                  className="w-full p-2 border border-gray-200 rounded-md"
                                  placeholder="Task title"
                                  value={newTaskTitle}
                                  onChange={(e) => setNewTaskTitle(e.target.value)}
                                />
                                <textarea
                                  className="w-full p-2 border border-gray-200 rounded-md resize-none"
                                  placeholder="Description (optional)"
                                  value={newTaskDesc}
                                  onChange={(e) => setNewTaskDesc(e.target.value)}
                                  rows={2}
                                />
                                <button
                                  className="bg-blue-500 text-white py-1 px-3 rounded-md hover:bg-blue-600 transition-colors"
                                  onClick={handleAddTask}
                                >
                                  Add Task
                                </button>
                              </div>
                            </div>
                            
                            <div className="flex-1 overflow-auto">
                              <h3 className="font-medium mb-2">Task List</h3>
                              {nodeTasks.length === 0 ? (
                                <div className="text-center text-gray-500 py-4">
                                  No tasks yet. Add one above!
                                </div>
                              ) : (
                                <div className="flex flex-col gap-2">
                                  {nodeTasks.map((task: any) => (
                                    <div key={task.id} className="border border-gray-200 rounded-md p-2">
                                      <div className="flex items-start gap-2">
                                        <input
                                          type="checkbox"
                                          checked={task.checked}
                                          onChange={() => handleToggleTask(task.id)}
                                          className="mt-1"
                                        />
                                        <div className="flex-1">
                                          <h4 className={`font-medium ${task.checked ? 'line-through text-gray-500' : ''}`}>
                                            {task.title}
                                          </h4>
                                          {task.description && (
                                            <p className={`text-sm ${task.checked ? 'text-gray-400' : 'text-gray-600'}`}>
                                              {task.description}
                                            </p>
                                          )}
                                          <div className="text-xs text-gray-500 mt-1">
                                            Created: {formatTimestamp(task.createdAt)}
                                            {task.checked && task.completedAt && (
                                              <>
                                                <br />
                                                Completed: {formatTimestamp(task.completedAt)}
                                                {task.completedBy && ` by ${task.completedBy}`}
                                              </>
                                            )}
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => handleDeleteTask(task.id)}
                                          className="text-red-500 hover:text-red-700 text-sm"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center text-gray-500">
                          <p>No node selected</p>
                          <p className="text-sm">Select a node to view its details</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Visual indicator when closed */}
            {(!panelState.isOpen || !shouldShowSidebar) && (
              <div className="h-full w-full border-r border-gray-200/30"></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SidebarPresence;
